import React, { useEffect, useState } from "react";
import api, { formatError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ensureAudio, playTone } from "../lib/sound";
import TimezoneSelect from "../components/TimezoneSelect";
import { tzDisplay, offsetLabel, fmtDateTime } from "../lib/timezone";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import { AlertTriangle, RotateCcw, BellRing, Send, Mail, Volume2, Bell, ShieldCheck, Globe, History, ClipboardList } from "lucide-react";
import { toast } from "sonner";
import { ScheduleRolesManager } from "../components/ScheduleRolesManager";

const PRIORITIES = ["normal", "important", "critical"];
const labelCls = "text-xs font-bold uppercase tracking-wider text-slate-500";
const AUDIT_LABEL = {
  org_timezone_changed: "Timezone changed",
  feedback_submitted: "Feedback submitted",
  feedback_status_changed: "Feedback status changed",
  feedback_assigned: "Feedback assigned",
  feedback_resolved: "Feedback resolved",
  screenshot_accessed: "Screenshot accessed",
  employee_shift_changed: "Employee shift changed",
  shift_renamed: "Shift renamed",
  shift_deleted: "Shift deleted",
  pilot_feedback_toggled: "Pilot Feedback toggled",
};

export default function OrganizationSettings() {
  const { user, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [reseed, setReseed] = useState(true);
  const [busy, setBusy] = useState(false);

  const [settings, setSettings] = useState(null);
  const [team, setTeam] = useState([]);
  const [org, setOrg] = useState(null);
  const [tzDraft, setTzDraft] = useState("");
  const [tzDialog, setTzDialog] = useState(false);
  const [tzBusy, setTzBusy] = useState(false);
  const [audits, setAudits] = useState([]);
  const [pilotFeedback, setPilotFeedback] = useState(false);
  const [pfBusy, setPfBusy] = useState(false);

  const loadOrg = () => api.get("/organization").then((r) => { setOrg(r.data); setTzDraft(r.data.timezone); setPilotFeedback(!!r.data.pilot_feedback_enabled); }).catch(() => {});
  const loadAudits = () => api.get("/admin/audit-logs?limit=25").then((r) => setAudits(r.data)).catch(() => {});

  useEffect(() => {
    api.get("/admin/notification-settings").then((r) => setSettings(r.data)).catch(() => {});
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
    loadOrg();
    loadAudits();
  }, []);

  const togglePilotFeedback = async (enabled) => {
    setPfBusy(true);
    setPilotFeedback(enabled);
    try {
      await api.patch("/admin/organization/pilot-feedback", { enabled });
      toast.success(enabled ? "Pilot Feedback enabled" : "Pilot Feedback disabled");
      await loadOrg();
      await loadAudits();
      if (refresh) await refresh();
    } catch (err) {
      setPilotFeedback(!enabled);
      toast.error(formatError(err.response?.data?.detail));
    } finally { setPfBusy(false); }
  };

  const saveTimezone = async () => {    setTzBusy(true);
    try {
      await api.patch("/admin/organization/timezone", { timezone: tzDraft });
      toast.success("Organization timezone updated");
      setTzDialog(false);
      await loadOrg();
      await loadAudits();
      if (refresh) await refresh();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setTzBusy(false); }
  };

  const supervisors = team.filter((u) => u.role === "supervisor" || u.role === "admin");

  const resetDialog = () => { setConfirmText(""); setReseed(true); };

  const doReset = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/reset-demo", { confirm: confirmText, reseed });
      toast.success(data.message || "Demo environment reset");
      setOpen(false); resetDialog();
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const saveSettings = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      const { data } = await api.put("/admin/notification-settings", patch);
      setSettings(data);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const togglePriority = (field, value) => {
    const cur = settings[field] || [];
    const next = cur.includes(value) ? cur.filter((v) => v !== value) : [...cur, value];
    saveSettings({ [field]: next });
  };

  const cleanInvalid = async () => {
    try {
      const { data } = await api.post("/admin/notifications/clean-invalid");
      toast.success(`Removed ${data.removed} invalid notification${data.removed === 1 ? "" : "s"}.`);
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
  };

  const runTest = async (channel) => {
    try {
      const { data } = await api.post("/admin/notifications/test", { channel });
      if (channel === "audible") {
        const ctx = ensureAudio();
        if (!ctx || ctx.state !== "running") {
          toast.error("Your browser blocked audible alerts. Open Notification Preferences and click Enable audible alerts.");
          return;
        }
        playTone("important", 0.7);
        toast.success("Played test tone");
      } else if (channel === "email") {
        toast.success(`Test email: ${data.email_status}`);
      } else {
        toast.success("Test in-app notification sent — check the bell");
      }
    } catch (err) {
      const code = err.response?.status;
      toast.error(code === 429 ? "Please wait a few seconds before testing again." : formatError(err.response?.data?.detail));
    }
  };

  const ModeButton = ({ value, title, desc }) => (
    <button type="button" data-testid={`reset-mode-${value ? "samples" : "empty"}`} onClick={() => setReseed(value)}
      className={`flex-1 rounded-md border p-3 text-left transition-colors ${reseed === value ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:border-slate-300"}`}>
      <p className="text-sm font-semibold text-slate-900">{title}</p>
      <p className="mt-0.5 text-xs text-slate-500">{desc}</p>
    </button>
  );

  const NumField = ({ label, field, hint }) => (
    <div>
      <Label className={labelCls}>{label}</Label>
      <Input
        data-testid={`notif-${field}`}
        type="number"
        min={0}
        value={settings?.[field] ?? 0}
        onChange={(e) => setSettings({ ...settings, [field]: Number(e.target.value) })}
        onBlur={(e) => saveSettings({ [field]: Number(e.target.value) })}
        className="mt-1.5 w-32"
      />
      {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
    </div>
  );

  return (
    <div className="animate-fade-up space-y-8">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-400">Administration</p>
        <h1 className="font-display text-3xl font-black tracking-tight text-slate-900">Organization Settings</h1>
        <p className="mt-1 text-sm text-slate-500">Configure notifications, escalation and demo controls for this fictional organization.</p>
      </div>

      {/* General — Timezone */}
      <Card className="space-y-4 rounded-md border border-slate-200 bg-white p-6 shadow-sm" data-testid="org-general-settings">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
          <Globe className="h-4 w-4 text-blue-600" /> General
        </h2>
        <div>
          <Label className={labelCls}>Organization timezone</Label>
          <p className="text-xs text-slate-400">All timestamps are stored in UTC and displayed in this timezone. Daylight-saving changes are applied automatically.</p>
          {org && (
            <p className="mt-2 text-sm text-slate-600" data-testid="current-timezone">
              Current: <span className="font-semibold text-slate-900">{tzDisplay(org.timezone)}</span>
              <span className="ml-2 text-xs text-slate-400">{offsetLabel(org.timezone)}</span>
            </p>
          )}
          <div className="mt-2 max-w-md">
            <TimezoneSelect value={tzDraft} onChange={setTzDraft} testid="settings-timezone-select" />
          </div>
          <Dialog open={tzDialog} onOpenChange={setTzDialog}>
            <DialogTrigger asChild>
              <Button data-testid="save-timezone-button" disabled={!tzDraft || tzDraft === org?.timezone} className="mt-3 bg-blue-600 hover:bg-blue-700 text-white">
                Save timezone
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Change organization timezone?</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-600">
                Displayed local times across handoffs, schedules, acknowledgements, notifications and history
                will now be shown in <span className="font-semibold">{tzDisplay(tzDraft)}</span>. The actual
                recorded moment of every existing record is preserved — only how it is displayed changes.
                This change is recorded in the admin audit log.
              </p>
              <DialogFooter>
                <Button variant="outline" onClick={() => setTzDialog(false)}>Cancel</Button>
                <Button data-testid="confirm-timezone-button" disabled={tzBusy} onClick={saveTimezone} className="bg-blue-600 hover:bg-blue-700 text-white">
                  {tzBusy ? "Saving…" : "Confirm change"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
          <div>
            <p className="text-sm font-semibold text-slate-800">Pilot Feedback</p>
            <p className="text-xs text-slate-500">Enable the in-app feedback &amp; bug report tools (Send Feedback button and the admin Pilot Feedback page) for this organization. New organizations have this off by default.</p>
          </div>
          <Switch data-testid="pilot-feedback-toggle" checked={pilotFeedback} disabled={pfBusy} onCheckedChange={togglePilotFeedback} />
        </div>
      </Card>

      {/* Schedule Roles */}
      <Card className="space-y-2 rounded-md border border-slate-200 bg-white p-6 shadow-sm" data-testid="schedule-roles-settings">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
          <ClipboardList className="h-4 w-4 text-blue-600" /> Schedule Roles
        </h2>
        <ScheduleRolesManager />
      </Card>

      {/* Notification settings */}
      <Card className="space-y-5 rounded-md border border-slate-200 bg-white p-6 shadow-sm" data-testid="org-notification-settings">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
          <BellRing className="h-4 w-4 text-blue-600" /> Notification & Escalation Settings
        </h2>
        {!settings ? <div className="h-24 animate-pulse rounded bg-slate-100" /> : (
          <>
            <div className="flex flex-wrap gap-6">
              <NumField label="Reminder time (min)" field="reminder_minutes" hint="Unacknowledged reminder delay." />
              <NumField label="Supervisor escalation (min)" field="escalation_minutes" hint="Set 0 for instant demo testing." />
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <Label className={labelCls}>Priorities that generate email</Label>
                <div className="mt-2 flex gap-2">
                  {PRIORITIES.map((pr) => (
                    <label key={pr} className="flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-sm capitalize">
                      <Checkbox data-testid={`email-pri-${pr}`} checked={(settings.email_priorities || []).includes(pr)} onCheckedChange={() => togglePriority("email_priorities", pr)} />
                      {pr}
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label className={labelCls}>Priorities that generate audible alerts</Label>
                <div className="mt-2 flex gap-2">
                  {PRIORITIES.map((pr) => (
                    <label key={pr} className="flex items-center gap-1.5 rounded-md border border-slate-200 px-3 py-1.5 text-sm capitalize">
                      <Checkbox data-testid={`audible-pri-${pr}`} checked={(settings.audible_priorities || []).includes(pr)} onCheckedChange={() => togglePriority("audible_priorities", pr)} />
                      {pr}
                    </label>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-between rounded-md border border-slate-100 p-4">
              <div>
                <p className="text-sm font-semibold text-slate-900">Allow muting Critical notifications</p>
                <p className="text-xs text-slate-500">When off, Critical alerts always reach every recipient.</p>
              </div>
              <Switch data-testid="critical-mutable-switch" checked={!!settings.critical_mutable} onCheckedChange={(v) => saveSettings({ critical_mutable: v })} />
            </div>
            <div>
              <Label className={labelCls}>Supervisors who receive escalations</Label>
              <p className="text-xs text-slate-400">Leave all unchecked to notify every authorized supervisor for the division.</p>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {supervisors.map((s) => (
                  <label key={s.id} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm">
                    <Checkbox
                      data-testid={`escalation-sup-${s.id}`}
                      checked={(settings.escalation_supervisors || []).includes(s.id)}
                      onCheckedChange={() => togglePriority("escalation_supervisors", s.id)}
                    />
                    {s.name} · {s.role}
                  </label>
                ))}
                {supervisors.length === 0 && <p className="text-sm text-slate-400">No supervisors yet.</p>}
              </div>
            </div>
          </>
        )}
      </Card>

      {/* Notification test */}
      <Card className="space-y-3 rounded-md border border-slate-200 bg-white p-6 shadow-sm" data-testid="notification-test-panel">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
          <Send className="h-4 w-4 text-blue-600" /> Notification Test
        </h2>
        <p className="text-sm text-slate-500">Send yourself a test on each channel to confirm delivery. (Rate limited.)</p>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="test-in-app" onClick={() => runTest("in_app")} variant="outline" className="gap-2"><Bell className="h-4 w-4" /> Test in-app</Button>
          <Button data-testid="test-email" onClick={() => runTest("email")} variant="outline" className="gap-2"><Mail className="h-4 w-4" /> Test email</Button>
          <Button data-testid="test-audible" onClick={() => runTest("audible")} variant="outline" className="gap-2"><Volume2 className="h-4 w-4" /> Test audible</Button>
          <Button data-testid="clean-invalid-notifications" onClick={cleanInvalid} variant="outline" className="gap-2 text-slate-700"><ShieldCheck className="h-4 w-4" /> Clean Invalid Notifications</Button>
        </div>
      </Card>

      {/* Demo controls — internal only: System Owner, or the dedicated demo organization */}
      {(user?.is_demo || user?.role === "system_admin") && (
      <Card className="rounded-md border border-red-200 bg-red-50/40 p-6 shadow-sm" data-testid="demo-controls">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-red-700">
          <AlertTriangle className="h-4 w-4" /> Demo Controls
        </h2>
        <p className="mt-2 max-w-2xl text-sm text-slate-600">
          Reset the demo environment to a clean state. This removes all fictional operational
          records while preserving users, roles, divisions, shifts, and organization settings.
        </p>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetDialog(); }}>
          <DialogTrigger asChild>
            <Button data-testid="reset-open-button" variant="outline" className="mt-4 gap-2 text-red-700 border-red-300 hover:bg-red-100">
              <RotateCcw className="h-4 w-4" /> Reset Demo Environment
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Reset Demo Environment</DialogTitle></DialogHeader>
            <p className="text-sm text-slate-600">
              This will remove all fictional handoffs, tasks, comments, acknowledgements, equipment
              issues, schedules, notifications, and archived records for this demo organization.
              Users, roles, divisions, shifts, and organization settings will be preserved.
            </p>
            <div className="mt-2">
              <p className={labelCls}>After reset</p>
              <div className="mt-2 flex gap-2">
                <ModeButton value={false} title="Empty environment" desc="No sample records." />
                <ModeButton value={true} title="Load 3 samples" desc="Dispatch, Patrol & Jail records." />
              </div>
            </div>
            <div className="mt-2">
              <p className="text-sm text-slate-600">Type <span className="font-bold">RESET DEMO</span> to confirm.</p>
              <Input data-testid="reset-confirm-input" value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Type RESET DEMO" className="mt-2" />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="reset-confirm-button" disabled={confirmText !== "RESET DEMO" || busy} onClick={doReset} className="bg-red-600 hover:bg-red-700 text-white">
                {busy ? "Resetting…" : "Reset Demo Environment"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Card>
      )}

      {/* Admin audit log */}
      <Card className="space-y-3 rounded-md border border-slate-200 bg-white p-6 shadow-sm" data-testid="audit-log-panel">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-slate-900">
          <History className="h-4 w-4 text-blue-600" /> Admin Audit Log
        </h2>
        <p className="text-sm text-slate-500">Recent administrative events (timezone changes, feedback actions, screenshot access).</p>
        <div className="space-y-2" data-testid="audit-log-list">
          {audits.length === 0 && <p className="text-sm text-slate-400">No audit events yet.</p>}
          {audits.map((a) => (
            <div key={a.id} className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-slate-100 px-3 py-2 text-sm">
              <div>
                <span className="font-semibold text-slate-800">{AUDIT_LABEL[a.action] || a.action}</span>
                {a.target_ref && <span className="ml-1 font-mono text-xs text-slate-500">{a.target_ref}</span>}
                {a.details?.from && a.details?.to && (
                  <span className="ml-2 text-xs text-slate-500">{a.details.from} → {a.details.to}</span>
                )}
                <span className="ml-2 text-xs text-slate-400">by {a.actor_name}</span>
              </div>
              <span className="text-xs text-slate-400">{fmtDateTime(a.created_at, user?.org_timezone)}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
